import React from "react";
import { Box, Paper, Typography, Button, Alert, Link } from "@mui/material";
import globals from "../../Globals";
import { PageContainer, SectionHeader, SelectorBar, SelectField, DataTable, StatCard } from "../../Layout/Common";
import { FIXED_TABLE } from "../Localization/loc";
import { war, savedBranch, saveBranch, canEdit, date, num, ConfirmAction } from "./community";

// Community → War (service `war`, Evil Tower; Docs/Guild.md §6 / §10): the map war of a branch as text - `war season <branch>` (the
// season, its day and turn, the chunks, the counts), `war ranking <branch>` (the guilds in the war in rank order, then the ones out),
// `war guild <id>` (a guild's castles and battles, picked from the ranking) - and the operator's two buttons: `war close-now <branch>`
// (the due battles closed now) and `war day-now <branch>` (today's day turn: the chests of today's played sets). No map rendering.
// `#war` views, `war` acts.
const short = (id) => (id ? String(id).slice(-8) : "—");

export default class WarTab extends React.Component {
    state = { branch: savedBranch(), season: null, ranking: null, guild: null, guildId: null, error: null, loading: false, busy: false, action: null, done: [] };

    componentDidMount() { this.load(); }

    load = () => {
        saveBranch(this.state.branch);
        this.setState({ loading: true, error: null });
        Promise.all([
            war("Loading the war season", ["season", this.state.branch], { silent: true }),
            war("Loading the war ranking", ["ranking", this.state.branch], { silent: true }),
        ])
            .then(([season, ranking]) => this.setState({ season: season && typeof season === "object" ? season : null, ranking: ranking && typeof ranking === "object" ? ranking : null },
                () => this.state.guildId && this.pick(this.state.guildId)))
            .catch(e => this.setState({ season: null, ranking: null, error: e?.message ?? "failed" }))
            .finally(() => this.setState({ loading: false }));
    }

    pick = (id) => {
        this.setState({ guildId: id });
        war("Loading the guild's war", ["guild", id], { silent: true })
            .then(r => this.setState({ guild: r && typeof r === "object" ? r : null }))
            .catch(e => this.setState({ guild: null, error: e?.message ?? "failed" }));
    }

    run = (verb, title, text, label) => this.setState({ action: {
        title, text, confirmText: label,
        run: () => {
            this.setState({ busy: true });
            return war(title, [verb, this.state.branch], { successMessage: "Done" })
                .then(r => { this.setState(s => ({ action: null, done: [{ at: new Date(), text: r?.done ?? JSON.stringify(r) }, ...s.done].slice(0, 5) })); this.load(); })
                .catch(() => {})
                .finally(() => this.setState({ busy: false }));
        },
    }});

    render() {
        const { branch, season, ranking, guild, guildId, error, loading, busy, action, done } = this.state;
        const edit = canEdit("war");
        const rows = [...(ranking?.ranked ?? []), ...(ranking?.outOfWar ?? [])];
        const names = Object.fromEntries(rows.map(g => [g.id, `${g.name} [${g.tag}]`]));
        const rankColumns = [
            { id: "rank", label: "Rank", width: "8%", align: "right", render: g => g.rank || "—" },
            { id: "name", label: "Guild", width: "30%", render: g => <Link component="button" variant="body2" onClick={() => this.pick(g.id)} title={g.id}>{g.name} [{g.tag}]</Link> },
            { id: "castles", label: "Castles", width: "10%", align: "right", render: g => num(g.castles) },
            { id: "level", label: "Level", width: "8%", align: "right" },
            { id: "members", label: "Members", width: "10%", align: "right" },
            { id: "color", label: "Color", width: "8%", align: "right" },
            { id: "home", label: "Home", width: "10%", render: g => g.home ?? "—" },
            { id: "state", label: "State", width: "16%", render: g => g.inWar ? (g.attackBattle ? "attacking" : "in the war") : g.closedAt ? "closed" : g.eliminatedAt ? `eliminated ${date(g.eliminatedAt)}` : "out" },
        ];
        const battleColumns = [
            { id: "castle", label: "Castle", width: "10%", render: b => `${b.q},${b.r}` },
            { id: "tier", label: "Tier", width: "6%", align: "right" },
            { id: "world", label: "World", width: "10%" },
            { id: "endsAt", label: "Ends", width: "16%", render: b => date(b.endsAt) },
            { id: "state", label: "State", width: "14%", render: b => b.closed ? `closed · ${b.winner ? `won by ${names[b.winner] ?? short(b.winner)}` : "no winner"}` : "running" },
            { id: "sides", label: "Sides (waves / played)", width: "44%", render: b => (b.sides ?? []).map(x => `${names[x.guild] ?? short(x.guild)}${x.defender ? " (def)" : ""}${x.late ? " (late)" : ""} ${num(x.waves)} / ${x.played}`).join(" · ") },
        ];
        const fullChunks = (season?.chunks ?? []).filter(c => c.full).length;
        return (
            <PageContainer>
                <SectionHeader title="War">
                    <Typography variant="body2" color="text.secondary">{globals.selectedTitle} · {branch} · the map war (Docs/Guild.md §6)</Typography>
                </SectionHeader>
                <SelectorBar>
                    <SelectField label="Branch" size="small" minWidth={160} value={branch} options={globals.branches} onChange={v => this.setState({ branch: v, guild: null, guildId: null }, this.load)} />
                    <Button variant="outlined" onClick={this.load} disabled={loading}>{loading ? "Loading…" : "Reload"}</Button>
                    {edit && <Button variant="contained" color="warning" disabled={busy}
                                     onClick={() => this.run("close-now", "Close the due battles now?", "Every battle of this branch past its end is resolved now (the scheduler does it every 5 minutes): the castles change hands, the lines are posted, guilds left without a castle are eliminated.", "Close now")}>Close due battles</Button>}
                    {edit && <Button variant="contained" color="warning" disabled={busy}
                                     onClick={() => this.run("day-now", "Turn the war day now?", "Today's day turn runs now: every member credited today gets the guild's war chest (once per account and day), the full chunks are re-checked, the war rows refreshed, guilds without a castle eliminated. The 03:00 UTC job does not give today twice.", "Turn the day")}>Turn the day</Button>}
                </SelectorBar>
                {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}
                {done.length > 0 && (
                    <Alert severity="success" sx={{ mb: 2 }} onClose={() => this.setState({ done: [] })}>
                        {done.map((d, i) => <div key={i}>{d.at.toLocaleString()}: {d.text}</div>)}
                    </Alert>
                )}
                {season && (
                    <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 2 }}>
                        <StatCard label="Season" value={season.season} secondary={`started ${date(season.startedAt)}`} tertiary={season.warTable ? "war table on" : "NO war table: the war is off"} tertiaryColor={season.warTable ? undefined : "error.main"} />
                        <StatCard label="Season day" value={season.day} secondary={`turns ${date(season.dayEndsAt)}`} tertiary={`last turn: day ${season.lastTurnDay}`} />
                        <StatCard label="Chunks" value={num((season.chunks ?? []).length)} secondary={`${num(season.castleCount)} castles · ${fullChunks} full`} />
                        <StatCard label="Guilds in the war" value={num(season.guildsInWar)} secondary={`${num(season.guildCount)} ever entered`} />
                        <StatCard label="Running battles" value={num(season.runningBattles)} secondary={season.nextDue ? `next due ${date(season.nextDue)}` : "none due"} tertiary={`stamp ${season.stamp}`} />
                    </Box>
                )}
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Ranking</Typography>
                {rows.length > 0
                    ? <DataTable sx={FIXED_TABLE} columns={rankColumns} rows={rows} getRowKey={g => g.id} />
                    : <Paper sx={{ p: 2 }}><Typography variant="body2" color="text.secondary">{ranking ? "No guild entered this season." : "Loading…"}</Typography></Paper>}
                {guildId && (
                    <>
                        <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
                            {guild ? `${guild.name} [${guild.tag}] · ${num(guild.castles)} castle(s): ${(guild.held ?? []).join("  ") || "—"} · played today: ${(guild.playedToday ?? []).length}` : `Guild ${short(guildId)}…`}
                        </Typography>
                        {guild?.battles?.length > 0
                            ? <DataTable sx={FIXED_TABLE} columns={battleColumns} rows={guild.battles} getRowKey={b => b.id} />
                            : <Paper sx={{ p: 2 }}><Typography variant="body2" color="text.secondary">No battle in the last 7 days.</Typography></Paper>}
                    </>
                )}
                <ConfirmAction action={action} busy={busy} onClose={() => this.setState({ action: null })} />
            </PageContainer>
        );
    }
}
